import ReactSelectWrapper, {
  REACT_SELECT_DEFAULT_CONTROL_HEIGHT,
} from 'components/FormUtils/ReactSelectWrapper'
import { colors } from 'styles/tokens'

export type SelectOption = { value: string; label: string }

/**
 * A single-select styled to match the CheckboxDropdown triggers in the reporting
 * control rows: same fixed height, text-sm value, and the `.form-control-select`
 * hairline (`line-form` border / `line-focus` focus) rather than react-select's
 * default grey. Shared by the builder's Group by / Break down by and the filter
 * bar's Period control so the styling lives in one place.
 *
 * @param ariaLabel - Accessible label for the select control.
 * @param options - The selectable options.
 * @param value - The currently selected option.
 * @param onChange - Called with the chosen option's value.
 */
export function MatchedSelect({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string
  options: SelectOption[]
  /** Undefined renders the placeholder — e.g. nothing linked yet. */
  value?: SelectOption
  onChange: (value: string) => void
}) {
  return (
    <ReactSelectWrapper
      aria-label={ariaLabel}
      isClearable={false}
      styles={{
        control: (base, state) => ({
          ...base,
          height: REACT_SELECT_DEFAULT_CONTROL_HEIGHT,
          minHeight: REACT_SELECT_DEFAULT_CONTROL_HEIGHT,
          borderRadius: 6,
          borderColor: state.isFocused
            ? colors['line-focus']
            : colors['line-form'],
          boxShadow: 'none',
          '&:hover': {
            borderColor: state.isFocused
              ? colors['line-focus']
              : colors['line-form'],
          },
        }),
        valueContainer: (base) => ({
          ...base,
          paddingTop: 0,
          paddingBottom: 0,
        }),
        dropdownIndicator: (base) => ({
          ...base,
          paddingTop: 0,
          paddingBottom: 0,
        }),
        singleValue: (base) => ({ ...base, fontSize: '0.875rem' }),
        placeholder: (base) => ({ ...base, fontSize: '0.875rem' }),
      }}
      options={options}
      value={value}
      onChange={(selected) => onChange((selected as SelectOption).value)}
    />
  )
}
